import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Colors,
  Icon,
  type IconProps,
  Icons,
  Typography,
  classNames,
  createClasses,
  spacing,
} from '@kp/react-ui';

const classes = createClasses({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('md'),
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing('xs'),
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing('xs'),
    minWidth: 0,
  },
  title: {
    margin: 0,
    color: Colors.Neutral.text,
  },
  lock: {
    color: Colors.Neutral.iconInactive,
    flexShrink: 0,
  },
  description: {
    color: Colors.Neutral.textWeak,
  },
});

export interface AppHeadingProps {
  icon: IconProps['icon'];
  iconColor: string;
  title: string;
  description: string;
  /** Shows a lock icon at the end of the title row. */
  locked?: boolean;
  className?: string;
}

/** Icon, title and description of an app card on Home. */
export const AppHeading: React.FC<AppHeadingProps> = ({
  icon,
  iconColor,
  title,
  description,
  locked = false,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.titleRow}>
        <div className={classes.titleGroup}>
          <Icon icon={icon} size={24} color={iconColor} />
          <Typography
            variant="titleSmall"
            component="h3"
            className={classes.title}
          >
            {title}
          </Typography>
        </div>
        {locked && (
          <Icon
            icon={Icons.IconLock}
            size={24}
            className={classes.lock}
            role="img"
            aria-label={t('home.apps.locked')}
          />
        )}
      </div>
      <Typography variant="bodySmall" className={classes.description}>
        {description}
      </Typography>
    </div>
  );
};
